import type { ThemeName } from "../app/types";
import type { ProjectDashboardCard } from "../viewmodel/projectsDashboardAdapter";
import { TechnicalDetails } from "../components/TechnicalDetails";
import { AiProvenanceBadge, cx, ThemeControl } from "./shared";
import { Term } from "./Glossary";
import {
  itemSummaryMetrics,
  physicalSummaryMetrics,
  projectNextAction,
  projectTechnicalChecks,
  projectTechnicalRawData,
  projectTechnicalSources,
  readinessClass
} from "./projectDashboardMetrics";

export function ProjectSummaryOnlyScreen({
  project,
  theme,
  hasSavedInputs,
  onTheme,
  onBack,
  onCreateProject
}: {
  project: ProjectDashboardCard;
  theme: ThemeName;
  hasSavedInputs: boolean;
  onTheme: (theme: ThemeName) => void;
  onBack: () => void;
  onCreateProject: () => void;
}) {
  const explainCopy = hasSavedInputs
    ? "This project's specification isn't stored here yet. The saved inputs are still available, so review the take-off and requirements before running Specify again."
    : "This project's specification isn't stored here yet. This project doesn't have its source documents stored here yet, so add files or paste the schedule before running Specify again.";
  const continueCopy = hasSavedInputs ? (
    <>
      Open the project inputs, check the <Term name="take-off">take-off</Term>
      and requirements, then run Specify to store a full workspace result.
    </>
  ) : (
    <>
      Add the source documents or paste the schedule first. Specify will create a full
      workspace result once those inputs are reviewed.
    </>
  );
  // RETIRE-H: this old summary CTA retired 2026-08-20 per Rich R4; it now
  // re-enters the upload-first stepper instead of the legacy create wizard.
  const ctaLabel = "Open upload-first stepper";

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-label="Project summary"
      className="min-h-screen px-8 py-8 xl:px-14"
    >
      <div className="w-full">
        <header className="border-b border-[var(--line)] pb-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <button
              type="button"
              onClick={onBack}
              className="cursor-pointer rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-3 py-2 text-[12px] font-bold text-[var(--muted)] hover:bg-[var(--chip)]"
            >
              Projects
            </button>
            <ThemeControl theme={theme} onTheme={onTheme} />
          </div>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[.11em] text-[var(--faint)]">
                Project overview
              </div>
              <h1 className="mt-1 text-[34px] font-extrabold leading-none tracking-[-.035em]">
                {project.name}
              </h1>
              <div className="mt-3 max-w-[760px] rounded-[11px] border border-[#E9D3AD] bg-[#F6EEDD] px-4 py-3 text-[12.5px] font-semibold leading-[1.5] text-[#8A6A2E]">
                {explainCopy}
              </div>
            </div>
            <div className="rounded-[13px] border border-[var(--line)] bg-[var(--surface)] px-5 py-4 shadow-[0_18px_42px_-36px_rgba(35,33,30,.85)]">
              <div className="text-[13px] font-extrabold text-[var(--ink2)]">
                Continue this project
              </div>
              <p className="mt-2 text-[12px] font-semibold leading-[1.55] text-[var(--muted)]">
                {continueCopy}
              </p>
              <button
                type="button"
                onClick={onCreateProject}
                className="mt-4 w-full cursor-pointer rounded-[10px] bg-[var(--ink)] px-4 py-3 text-[12px] font-extrabold text-[var(--onSolid)] shadow-[0_18px_40px_-30px_rgba(35,33,30,.85)]"
              >
                {ctaLabel}
              </button>
            </div>
          </div>
        </header>

        <section className="grid gap-3 py-6 md:grid-cols-3 xl:grid-cols-5">
          {[
            ["Apartment types", project.apartmentTypeCount],
            ["Homes", project.apartmentCount],
            ["Controllers", project.controllerCount],
            ["Devices", project.deviceCount],
            ["Infrastructure", project.infraCount],
            ["Item lines", project.bomRowCount],
            ["BOM units", project.bomUnitCount],
            ["Need your decision", project.needsUserDecisionCount],
            ["Blocking risks", project.blockingRiskCount]
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-[12px] border border-[var(--line)] bg-[var(--surface)] px-4 py-3"
            >
              <div className="text-[10px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                {label}
              </div>
              <div className="mt-1 text-[27px] font-extrabold tracking-[-.035em]">
                {value}
              </div>
            </div>
          ))}
        </section>

        <section className="grid gap-4 lg:grid-cols-[1.15fr_.85fr]">
          <div className="rounded-[13px] border border-[var(--line)] bg-[var(--surface)] px-5 py-4">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span
                className={cx(
                  "rounded-full px-2.5 py-1 text-[10.5px] font-bold",
                  readinessClass(project.exportReadiness)
                )}
              >
                {project.readinessLabel}
              </span>
            </div>
            <div className="rounded-[10px] bg-[var(--raised2)] px-3 py-3 text-[13px] font-extrabold leading-[1.4] text-[var(--ink2)]">
              {projectNextAction(project)}
            </div>
            <div className="mt-4 text-[10px] font-bold uppercase tracking-[.06em] text-[var(--faint)]">
              Metrics
            </div>
            <div className="mt-2 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
              {[...physicalSummaryMetrics(project), ...itemSummaryMetrics(project)].map(
                ([label, value]) => (
                  <div
                    key={label}
                    className="rounded-[10px] bg-[var(--raised2)] px-3 py-2"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[.06em] text-[var(--faint)]">
                      {label}
                    </div>
                    <div className="mt-0.5 text-[18px] font-extrabold">{value}</div>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-[12px] border border-[#D7E1EA] bg-[#F6FAFD] px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[.08em] text-[#3E6E9A]">
                    Edge capabilities
                  </div>
                  <div className="mt-1 text-[11px] font-extrabold text-[#3E6E9A]">
                    Included by default
                  </div>
                </div>
                <AiProvenanceBadge
                  why={project.edgeInclusionWhy}
                  where={project.edgeInclusionWhere}
                  defaulted={project.edgeDefaultApplied}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {project.edgeCapabilities.map((capability) => (
                  <span
                    key={`${project.id}-${capability}`}
                    className="rounded-md bg-[var(--surface)] px-2 py-1 text-[11px] font-bold text-[var(--muted)]"
                  >
                    {capability}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-[13px] border border-[var(--line)] bg-[var(--surface)] px-5 py-4">
            <TechnicalDetails
              idPrefix={`summary-${project.id}-technical-details`}
              sources={projectTechnicalSources(project)}
              checks={projectTechnicalChecks(project)}
              rawData={projectTechnicalRawData(project)}
              data-testid={`summary-${project.id}-technical-details`}
            />
          </div>
        </section>
      </div>
    </main>
  );
}
